import {useMemo} from 'react';
import {ChevronRight, Compass, Store} from 'lucide-react';
import {useDesktop} from '../desktop/DesktopContext';
import {money} from '../lib/format';
import {rememberLaunchOrigin} from '../mobile/sheetMotion';
import {useAppLink} from '../shell/AppLink';
import {useStore} from '../store/StoreContext';
import {
  LISTING_TYPE,
  PURPOSE,
  coverPhoto,
  has360,
  isPublic,
  type Listing,
} from '../store/types';
import {Picture} from './Picture';

interface MarketWidgetProps {
  className?: string;
}

export function MarketWidget({className = ''}: MarketWidgetProps) {
  const {listings, ready} = useStore();
  const {openApp} = useDesktop();
  const {open} = useAppLink();

  const publicListings = useMemo(() => {
    return listings
      .filter((l) => isPublic(l))
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [listings]);

  const featured = publicListings[0] as Listing | undefined;
  const recentItems = publicListings.slice(1, 3);

  const handleOpenMarket = (e: React.MouseEvent<HTMLElement>, listingId?: string) => {
    e.stopPropagation();
    rememberLaunchOrigin('market', e.currentTarget);
    if (listingId) {
      open('market', {listingId});
    } else {
      openApp('market');
    }
  };

  if (!ready || !featured) {
    return null;
  }

  const featuredCover = coverPhoto(featured);

  return (
    <div
      data-app-icon="market"
      onPointerDown={(e) => rememberLaunchOrigin('market', e.currentTarget)}
      onClick={(e) => handleOpenMarket(e)}
      className={`group relative cursor-pointer overflow-hidden rounded-[26px] p-4 transition-all duration-200 active:scale-[0.985] ${className}`}
      style={{
        background: 'var(--color-surface, #ffffff)',
        boxShadow:
          '0 10px 25px -5px rgba(0, 0, 0, 0.06), 0 8px 10px -6px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
        border: '1px solid var(--color-line, rgba(0,0,0,0.08))',
      }}
    >
      {/* iOS Widget Header */}
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[8px] bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-sm">
            <Store size={14} strokeWidth={2.2} />
          </div>
          <span className="truncate text-[13px] font-extrabold tracking-tight text-ink">
            Thị trường
          </span>
          <span className="shrink-0 flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold text-emerald-600 dark:text-emerald-400">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
            Mới nhất
          </span>
        </div>

        <div className="flex shrink-0 items-center gap-0.5 text-[11px] font-bold text-ink-3 transition-colors group-hover:text-emerald-600">
          <span>Xem tất cả</span>
          <ChevronRight size={13} strokeWidth={2.2} />
        </div>
      </div>

      {/* Hero Featured Listing */}
      <div
        onClick={(e) => handleOpenMarket(e, featured.id)}
        className="flex flex-col sm:flex-row gap-3 rounded-[18px] p-2.5 transition-all bg-[var(--color-sunken,rgba(0,0,0,0.03))] hover:bg-[var(--color-sunken,rgba(0,0,0,0.06))]"
      >
        {/* Cover Photo */}
        <div className="relative h-28 sm:h-24 sm:w-36 shrink-0 overflow-hidden rounded-[14px]">
          <Picture
            refItem={featuredCover?.ref ?? null}
            alt={featured.title}
            ratio="16 / 10"
            className="h-full w-full object-cover"
          />
          {has360(featured) && (
            <div className="absolute bottom-1.5 left-1.5 flex items-center gap-1 rounded-md bg-black/65 px-1.5 py-0.5 text-[10px] font-bold text-white backdrop-blur-md">
              <Compass size={11} className="animate-spin text-emerald-400" style={{animationDuration: '8s'}} />
              <span>360°</span>
            </div>
          )}
          <div className="absolute top-1.5 left-1.5 rounded-md bg-emerald-600 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-white shadow-sm">
            {PURPOSE[featured.purpose]}
          </div>
        </div>

        {/* Info */}
        <div className="flex flex-1 flex-col justify-between min-w-0 py-0.5">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[16px] font-black text-emerald-600 dark:text-emerald-400">
                {money(featured.price)}
              </span>
              <span className="text-[11px] font-semibold text-ink-3">
                {featured.area} m² • {LISTING_TYPE[featured.type]}
              </span>
            </div>
            <h4 className="mt-0.5 line-clamp-1 text-[13px] font-bold text-ink">
              {featured.title}
            </h4>
          </div>

          <div className="mt-2 flex items-center justify-between text-[11px] text-ink-3">
            <span className="truncate font-medium">
              {featured.district}, {featured.province}
            </span>
            {featured.bedrooms > 0 && (
              <span className="shrink-0 font-semibold">{featured.bedrooms} PN</span>
            )}
          </div>
        </div>
      </div>

      {/* Secondary Recent Items (Quick peek rows) */}
      {recentItems.length > 0 && (
        <div className="mt-2.5 grid grid-cols-2 gap-2">
          {recentItems.map((item) => {
            const cover = coverPhoto(item);
            return (
              <button
                key={item.id}
                type="button"
                onClick={(e) => handleOpenMarket(e, item.id)}
                className="flex items-center gap-2 rounded-[14px] p-2 text-left transition-colors bg-[var(--color-sunken,rgba(0,0,0,0.02))] hover:bg-[var(--color-sunken,rgba(0,0,0,0.05))]"
              >
                <div className="relative h-11 w-11 shrink-0 overflow-hidden rounded-[10px]">
                  <Picture
                    refItem={cover?.ref ?? null}
                    alt={item.title}
                    ratio="1 / 1"
                    className="h-full w-full object-cover"
                  />
                  {has360(item) && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40 text-white">
                      <Compass size={12} className="text-emerald-300" />
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[12px] font-black text-emerald-600 dark:text-emerald-400">
                    {money(item.price)}
                  </div>
                  <div className="truncate text-[10px] font-medium text-ink-2">
                    {item.district}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
